import { useMemo } from 'react'
import { inputClass } from '../../common/Field'
import { useWizard } from '../../../context/WizardContext'
import { parseItems, ITEM_LIMITS } from '../../../utils/items'
import { FIELDS, MESSAGES } from '../../../utils/copy'

/**
 * Step 4 — one box, and a running answer underneath it.
 *
 * This replaces four inputs per item, repeated: name, quantity, price, notes, with a
 * plus button to add another row. That is sixteen controls for four things, and every one
 * of them a place to be halfway through.
 *
 * **The list underneath is not decoration.** It is the only way the customer can tell
 * that `3 chicken` was read as three chickens rather than as a thing called "3 chicken" —
 * and it teaches the syntax without a single line of instruction, because the second time
 * somebody types a number they can see what it did the first time.
 */
export default function ItemsStep() {
  const { state, errors, setField } = useWizard()

  // Re-parsed on every keystroke, which is the point. It is a string split and a regex
  // over at most fifty short lines.
  const items = useMemo(() => parseItems(state.itemsText), [state.itemsText])
  const atCap = items.length >= ITEM_LIMITS.maxItems

  return (
    <div className="grid gap-4">
      <div>
        <label htmlFor="itemsText" className="block text-label font-semibold uppercase text-ink-2 mb-1.5">
          {FIELDS.items}
          <span className="text-alert ml-0.5">*</span>
        </label>

        <textarea
          id="itemsText"
          rows={6}
          value={state.itemsText}
          onChange={(event) => setField('itemsText', event.target.value)}
          placeholder={'3 chicken\n2 kilo bigas\n1 suka'}
          aria-invalid={Boolean(errors.itemsText)}
          aria-describedby={errors.itemsText ? 'itemsText-error' : 'itemsText-preview'}
          className={`${inputClass(errors.itemsText)} resize-y leading-relaxed`}
        />

        {errors.itemsText && (
          <p id="itemsText-error" role="alert" className="text-body-s text-alert mt-[5px]">
            {errors.itemsText}
          </p>
        )}
      </div>

      <section
        id="itemsText-preview"
        // Polite rather than assertive: it updates on every keystroke, and a screen reader
        // interrupting itself letter by letter is worse than saying nothing.
        aria-live="polite"
        className="border border-hairline rounded-card bg-surface-2 px-3.5 py-3"
      >
        <h2 className="text-label font-semibold uppercase text-ink-2">
          {items.length > 0 ? MESSAGES.itemsSoFar : MESSAGES.noItemsYet}
        </h2>

        {items.length > 0 && (
          <ol className="mt-2 grid gap-1">
            {items.map((item, index) => (
              <li key={index} className="flex items-baseline gap-2 text-body text-ink">
                <span className="font-semibold tabular-nums text-brand min-w-[2ch]">{item.quantity}</span>
                <span className="min-w-0 break-words">{item.name}</span>
              </li>
            ))}
          </ol>
        )}

        {atCap && (
          <p className="mt-2 text-body-s text-ink-3">
            {ITEM_LIMITS.maxItems} na ang pinakamarami. Hindi na mabibilang ang sumunod.
          </p>
        )}
      </section>
    </div>
  )
}
